// frontend/src/components/ProcurementChart.tsx
import { useState, useMemo, FC } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
    Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    ReferenceLine, Area, ComposedChart
} from 'recharts';
import { Box, Typography, Button, ToggleButtonGroup, ToggleButton, Alert, Collapse, Skeleton, Stack } from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import axiosInstance from '../../config/axiosInstance'; 

// --- TIPOS (Espejo del Backend) ---
interface ChartDataPoint {
    date: string;
    internal_price: number;
    market_price: number;
    internal_index: number;
    market_index: number;
}

interface SuggestionMetadata {
    detected_correlation: number;
    suggested_beta: number;
    suggested_lag: number;
    confidence: 'high' | 'medium' | 'low';
}

interface ChartResponse {
    family_name: string;
    ticker_name: string | null;
    currency: string;
    period: string;
    data: ChartDataPoint[];
    suggestion: SuggestionMetadata | null;
}

interface Props {
    familyId: string;
}

const CustomTooltip = ({ active, payload, label, currency }: any) => {
    if (active && payload && payload.length) {
        return (
            <Box sx={{ bgcolor: 'rgba(15, 23, 42, 0.9)', border: '1px solid #334155', p: 1.5, borderRadius: 1 }}>
                <Typography variant="caption" sx={{ color: '#94a3b8', mb: 1, display: 'block' }}>{label}</Typography>
                {payload.map((entry: any) => (
                    <Box key={entry.name} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: 0.5 }}>
                        <Typography variant="body2" sx={{ color: entry.color, fontWeight: 700 }}>
                            {entry.name === 'internal_index_adj' ? 'Nosotros' : 'Mercado'}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#f8fafc', fontFamily: 'monospace' }}>
                            {entry.payload.raw_internal ? 
                                (entry.name.includes('internal') ? 
                                    `${entry.payload.raw_internal.toFixed(2)} ${currency}` : 
                                    (entry.payload.raw_market ? `${entry.payload.raw_market.toFixed(2)}` : 'N/A')
                                ) : `${entry.value.toFixed(1)} pts`
                            }
                        </Typography>
                    </Box>
                ))}
                <Typography variant="caption" sx={{ color: '#64748b', mt: 1, display: 'block', fontStyle: 'italic' }}>
                    *Valores normalizados (Base 100)
                </Typography>
            </Box>
        );
    }
    return null;
};

const ProcurementChart: FC<Props> = ({ familyId }) => {
    const [period, setPeriod] = useState('12m');
    const [applyBeta, setApplyBeta] = useState(false);

    // 1. DATA FETCHING
    const { data, isLoading, isError } = useQuery({
        queryKey: ['chart', familyId, period],
        queryFn: async () => (await axiosInstance.get<ChartResponse>(`/procurement/dashboard/chart/${familyId}?period=${period}`)).data,
        staleTime: 1000 * 60 * 5 // 5 minutos cache
    });

    // 2. DATA PROCESSING (Smart Beta Application)
    const processedData = useMemo(() => {
        if (!data?.data) return [];
        // AÑADIDO TIPO EXPLÍCITO: (point: ChartDataPoint)
        return data.data.map((point: ChartDataPoint) => {
            let mktIdx = point.market_index;
            if (applyBeta && data.suggestion) {
                const delta = point.market_index - 100;
                mktIdx = 100 + (delta * data.suggestion.suggested_beta);
            }
            return {
                name: point.date,
                internal_index_adj: point.internal_index,
                market_index_adj: mktIdx,
                raw_internal: point.internal_price,
                raw_market: point.market_price
            };
        });
    }, [data, applyBeta]);

    if (isLoading) return <Skeleton variant="rectangular" height={300} sx={{ bgcolor: 'rgba(255,255,255,0.05)', borderRadius: 2 }} />;
    if (isError || !data) return <Alert severity="error">No se pudieron cargar los datos de evolución.</Alert>;
    if (data.data.length === 0) return <Alert severity="info" variant="outlined" sx={{ borderColor: '#334155', color: '#94a3b8' }}>Insuficientes datos históricos para generar gráfico de convergencia.</Alert>;

    const hasSuggestion = !!(data.suggestion && data.suggestion.confidence !== 'low' && data.suggestion.detected_correlation > 0.4);

    return (
        <Box sx={{ width: '100%', p: 2, bgcolor: '#0f172a', borderRadius: 2, border: '1px solid #1e293b' }}>
            
            {/* HEADER & CONTROLS */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <Box>
                    <Typography variant="h6" sx={{ color: '#e2e8f0', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ShowChartIcon sx={{ color: '#38bdf8' }} />
                        Análisis de Convergencia
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#64748b' }}>
                        Base 100 = Inicio del periodo ({data.period.toUpperCase()})
                    </Typography>
                </Box>

                <ToggleButtonGroup
                    value={period}
                    exclusive
                    onChange={(_, newP) => newP && setPeriod(newP)}
                    size="small"
                    sx={{ '& .MuiToggleButton-root': { color: '#94a3b8', borderColor: '#334155', '&.Mui-selected': { color: '#38bdf8', bgcolor: 'rgba(56, 189, 248, 0.1)' } } }}
                >
                    <ToggleButton value="12m">12M</ToggleButton>
                    <ToggleButton value="24m">24M</ToggleButton>
                    <ToggleButton value="all">HIST</ToggleButton>
                </ToggleButtonGroup>
            </Box>

            {/* INTELLIGENCE BADGE (Option B) */}
            <Collapse in={hasSuggestion && !applyBeta}>
                <Alert 
                    severity="info" 
                    variant="outlined"
                    icon={<AutoFixHighIcon />}
                    action={
                        <Button color="inherit" size="small" onClick={() => setApplyBeta(true)}>
                            Aplicar Ajuste
                        </Button>
                    }
                    sx={{ mb: 2, bgcolor: 'rgba(56, 189, 248, 0.05)', borderColor: 'rgba(56, 189, 248, 0.3)', color: '#bae6fd' }}
                >
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Correlación detectada: {(data.suggestion?.detected_correlation || 0) * 100}%
                    </Typography>
                    <Typography variant="caption">
                        Tus precios reaccionan al {(data.suggestion?.suggested_beta || 0) * 100}% de los movimientos del mercado. 
                        ¿Deseas ajustar el gráfico para ver la brecha real?
                    </Typography>
                </Alert>
            </Collapse>

            {/* CHART LIENZO */}
            <Box sx={{ width: '100%', height: 350 }}>
                <ResponsiveContainer>
                    <ComposedChart data={processedData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                        <defs>
                            <linearGradient id="colorInternal" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#38bdf8" stopOpacity={0.3}/>
                                <stop offset="95%" stopColor="#38bdf8" stopOpacity={0}/>
                            </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
                        <XAxis dataKey="name" stroke="#64748b" fontSize={12} tickFormatter={(val) => val.substring(0, 7)} />
                        <YAxis stroke="#64748b" fontSize={12} domain={['auto', 'auto']} />
                        <Tooltip content={<CustomTooltip currency={data.currency} />} />
                        
                        {/* LINEA DE REFERENCIA 100 */}
                        <ReferenceLine y={100} stroke="#475569" strokeDasharray="3 3" />

                        {/* SOMBRA DE ÁREA (Visual Gap) */}
                        <Area type="monotone" dataKey="internal_index_adj" stroke="none" fill="url(#colorInternal)" />

                        {/* LINEA MERCADO (Benchmark) */}
                        <Line 
                            type="monotone" 
                            dataKey="market_index_adj" 
                            stroke={applyBeta ? "#a78bfa" : "#f472b6"} // Morado si ajustado, Rosa si crudo
                            strokeWidth={2} 
                            dot={false}
                            strokeDasharray={applyBeta ? "5 5" : "0"} // Punteada si es ajustada
                            name="Mercado"
                        />

                        {/* LINEA INTERNA (Realidad) */}
                        <Line 
                            type="monotone" 
                            dataKey="internal_index_adj" 
                            stroke="#38bdf8" 
                            strokeWidth={3} 
                            dot={{ r: 4, fill: '#0f172a', strokeWidth: 2 }}
                            activeDot={{ r: 6 }}
                            name="Nosotros"
                        />
                    </ComposedChart>
                </ResponsiveContainer>
            </Box>
            
            {/* FOOTER LEGEND */}
            <Stack direction="row" spacing={3} justifyContent="center" sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#38bdf8' }} />
                    <Typography variant="caption" sx={{ color: '#94a3b8' }}>Mi Precio ({data.currency})</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: applyBeta ? "#a78bfa" : "#f472b6" }} />
                    <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                        {applyBeta ? `Mercado (Impacto ${(data.suggestion?.suggested_beta || 0)*100}%)` : 'Mercado (Crudo)'}
                    </Typography>
                </Box>
            </Stack>
        </Box>
    );
};

export default ProcurementChart;